/**
 * Copyright 2019-2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useEffect, useState, forwardRef } from 'react';
import NextImage from '@/components/NextImage';
import styles from '../../styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { closeIcon } from '@/constants/images/closeIcon';
import { eventConstants } from '@/constants/event';
import cx from 'classnames';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { routePaths } from '@/constants/routePaths';

const labelMap = {
  menu: 'label_Homepage_content_Menu',
  close: 'label_Homepage_content_Close',
  menuAriaLabel: 'label_Homepage_content_MenuAriaLabel',
};

const hamburgerIcon = {
  src: '/images/hamburger_icon.svg',
  alt: '',
};

export type MenuNavButtonProps = {
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleClose: (options: { focusMain: boolean }) => void;
  dataTestIdBtn?: string;
  open: boolean;
  id?: string;
  isFixedHeader?: boolean;
};

export const MenuNavButton = forwardRef<HTMLButtonElement, MenuNavButtonProps>(
  ({ handleClick, handleClose, open, dataTestIdBtn, id, isFixedHeader }, ref) => {
    const labels = useLabels(labelMap);
    const [focus, setFocus] = useState(false);
    const { asPath } = useLegacyRouter();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));

    useEffect(() => {
      if (focus && ref && 'current' in ref && ref.current) {
        ref.current.focus();
      }
    });

    useEffect(() => {
      return () => {
        handleClose({
          focusMain: !(isMobile && asPath.includes(routePaths.writeReviewPath)),
        });
      };
    }, [asPath, handleClose, isMobile]);

    let menuLbl = labels.menu;
    let buttonClick = handleClick;
    let menuImg = (
      <div
        data-testid="menu-nav"
        className={cx(styles.menuImgOpen, {
          [styles.menuImgOpenSticky]: isFixedHeader,
        })}
      >
        <img
          src={hamburgerIcon.src}
          alt={hamburgerIcon.alt}
          data-testid="mobileMenuLogo"
          height={15}
          width={20}
          decoding="async"
        />
      </div>
    );

    if (open) {
      buttonClick = () => handleClose({ focusMain: false });
      menuLbl = labels.close;
      menuImg = (
        <div className={styles.menuImgClose}>
          <NextImage src={closeIcon.src} alt={closeIcon.alt} height={16} width={20} />
        </div>
      );
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === eventConstants.escKeyType) {
        setFocus(true);
      }
      if (e.key === eventConstants.tabKeyType) {
        setFocus(false);
      }
    };

    return (
      <button
        id={id}
        ref={ref}
        data-testid={dataTestIdBtn}
        onClick={buttonClick}
        onKeyDown={handleOnKeyDown}
        onMouseDown={(e) => setTimeout(() => e.stopPropagation(), 200)}
        aria-expanded={open}
        aria-label={labels.menuAriaLabel}
        className={cx(styles.menuNavButton, {
          [styles.menuNavButtonSticky]: isFixedHeader,
        })}
      >
        <div aria-hidden>
          {menuImg}
          <div className={cx(styles.menuText, { [styles.menuTextSticky]: isFixedHeader })}>
            {menuLbl}
          </div>
        </div>
      </button>
    );
  },
);

MenuNavButton.displayName = 'MenuNavButton';

export default MenuNavButton;
